/**
 * Layout JSON -> DOM dönüşüm motoru + tema apply + sayfa rotation.
 *
 * Public API:
 *   - applyTheme(theme): :root'a --fp-color-*, --fp-font-* CSS değişkenleri yazar.
 *   - renderLayout(layout, root): DOM'u kurar, rotation enabled ise startRotation tetikler.
 *   - updateTick(symbol, tick): tickStore.update wrapper.
 *   - teardown(): rotation timer iptal + tickStore temizle.
 *
 * Component'ler side-effect import ile global custom element registry'ye kaydedilir.
 */
import { tickStore, type TickLike } from './tick-store.js';
import { pageState } from '../state/page-state.js';
import '../components/fp-header.js';
import '../components/fp-symbol-cell.js';
import '../components/fp-symbol-grid.js';
import '../components/fp-symbol-list.js';
import '../components/fp-symbol-ticker.js';
import '../components/fp-marquee.js';
import '../components/fp-widget-bar.js';
import '../components/fp-promo-image.js';
import { assetUrl } from '../assets.js';

export interface ThemeColors {
    bg: string;
    surface: string;
    fg: string;
    fg_muted: string;
    up: string;
    down: string;
    neutral: string;
}

export interface ThemeFonts {
    primary: string;
    numeric: string;
    size_base: number;
}

export interface Theme {
    v: number;
    name: string;
    colors: ThemeColors;
    fonts: ThemeFonts;
    radius?: { sm?: number; md?: number; lg?: number };
    spacing_scale?: number;
    surface_alpha?: number;
}

export type AreaWeight = 'auto' | 'small' | 'medium' | 'large' | 'xlarge';

export type LayoutComponent =
    | { type: 'header'; title_source?: string; title_custom?: string; logo?: { asset_id?: string | null; position?: string }; widgets?: string[]; weather_city?: string }
    | { type: 'symbol_grid'; columns?: number; rows?: number; text_size?: 'auto' | 'large' | 'xlarge'; name_style?: 'plain' | 'band'; area?: AreaWeight; symbols: string[]; show?: string[]; animate?: boolean }
    | { type: 'symbol_list'; columns?: number; area?: AreaWeight; symbols: string[]; column_symbols?: string[][]; show?: string[]; show_header?: boolean; text_size?: 'auto' | 'large' | 'xlarge' }
    | { type: 'symbol_ticker'; symbols: string[]; speed?: number; show?: string[]; text_size?: 'auto' | 'large' | 'xlarge' }
    | { type: 'marquee'; source: 'announcement' | 'custom'; text?: string; speed?: number; text_size?: 'auto' | 'large' | 'xlarge' }
    | { type: 'widget_bar'; widgets: string[] }
    | {
          type: 'promo_image';
          asset_id: string;
          fit?: 'contain' | 'cover' | 'fill';
          placement?: 'full' | 'top' | 'bottom' | 'left' | 'right';
          size?: 'small' | 'medium' | 'large';
      };

export interface LayoutPage {
    id: string;
    duration_sec?: number;
    components: LayoutComponent[];
}

export interface LayoutBackground {
    asset_id?: string | null;
    opacity?: number;
    fit?: 'cover' | 'contain' | 'fill';
    overlay?: number;
    blur?: number;
}

export interface LayoutSurface {
    opacity?: number;
    blur?: number;
}

export interface Layout {
    v: number;
    name: string;
    orientation: 'landscape' | 'portrait';
    theme_id?: string | null;
    pages: LayoutPage[];
    rotation?: { enabled?: boolean; interval_sec?: number };
    background?: LayoutBackground;
    surface?: LayoutSurface;
}

/* === Theme === */

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
    const c = theme.colors;
    root.style.setProperty('--fp-color-bg', c.bg);
    root.style.setProperty('--fp-color-surface', c.surface);
    root.style.setProperty('--fp-color-fg', c.fg);
    root.style.setProperty('--fp-color-fg-muted', c.fg_muted);
    root.style.setProperty('--fp-color-up', c.up);
    root.style.setProperty('--fp-color-down', c.down);
    root.style.setProperty('--fp-color-neutral', c.neutral);
    root.style.setProperty('--fp-font-primary', theme.fonts.primary);
    root.style.setProperty('--fp-font-numeric', theme.fonts.numeric);
    root.style.setProperty('--fp-font-size-base', `${theme.fonts.size_base}px`);

    // Panel opaklığı varsayılanı (layout surface.opacity bunu geçersiz kılabilir, bkz applyBackground)
    root.style.setProperty('--fp-theme-surface-alpha', String(theme.surface_alpha ?? 1));
    // Kontrast perdesi rengi = temanın TEMEL rengi → busy görseli tema paletine doğru
    // harmanlar (siyah/beyaz yerine). Açık temada yumuşak, koyu temada koyu; her durumda
    // paneller tema rengine oturduğundan okunabilirlik artar.
    root.style.setProperty('--fp-bg-overlay-color', c.bg);

    // body fallback
    document.body.style.background = 'var(--fp-color-bg)';
    document.body.style.color = 'var(--fp-color-fg)';
    document.body.style.fontFamily = 'var(--fp-font-primary)';
    document.body.style.fontSize = 'var(--fp-font-size-base)';
}


/* === Component factory === */

function renderComponent(comp: LayoutComponent): HTMLElement {
    switch (comp.type) {
        case 'header': {
            const el = document.createElement('fp-header') as HTMLElement & { widgets: string[] };
            if (comp.title_source) el.setAttribute('title-source', comp.title_source);
            if (comp.title_custom) el.setAttribute('title-custom', comp.title_custom);
            if (comp.logo?.asset_id) el.setAttribute('logo-asset', comp.logo.asset_id);
            if (comp.logo?.position) el.setAttribute('logo-position', comp.logo.position);
            el.widgets = comp.widgets ?? [];
            if (comp.weather_city) el.setAttribute('weather-city', comp.weather_city);
            return el;
        }
        case 'symbol_grid': {
            const el = document.createElement('fp-symbol-grid') as HTMLElement & {
                columns: number; textSize: string; nameStyle: string; symbols: string[]; show: string[];
            };
            el.columns = comp.columns ?? 3;
            el.textSize = comp.text_size ?? 'auto'; // satır sayısı artık otomatik (rows yok sayılır)
            el.nameStyle = comp.name_style ?? 'plain';
            el.symbols = comp.symbols;
            el.show = comp.show ?? ['bid', 'ask', 'direction'];
            return el;
        }
        case 'symbol_list': {
            const el = document.createElement('fp-symbol-list') as HTMLElement & {
                columns: number; symbols: string[]; columnSymbols: string[][]; show: string[]; showHeader: boolean; textSize: string;
            };
            el.columns = comp.columns ?? 1;
            el.symbols = comp.symbols;
            el.columnSymbols = comp.column_symbols ?? [];
            el.show = comp.show ?? ['bid', 'ask', 'direction'];
            el.showHeader = comp.show_header ?? false;
            el.textSize = comp.text_size ?? 'auto';
            return el;
        }
        case 'symbol_ticker': {
            const el = document.createElement('fp-symbol-ticker') as HTMLElement & {
                symbols: string[]; speed: number; show: string[]; textSize: string;
            };
            el.symbols = comp.symbols;
            el.speed = comp.speed ?? 60;
            el.show = comp.show ?? ['bid'];
            el.textSize = comp.text_size ?? 'auto';
            return el;
        }
        case 'marquee': {
            const el = document.createElement('fp-marquee') as HTMLElement & {
                source: string; text: string; speed: number; textSize: string;
            };
            el.source = comp.source;
            el.text = comp.text ?? '';
            el.speed = comp.speed ?? 50;
            el.textSize = comp.text_size ?? 'auto';
            return el;
        }
        case 'widget_bar': {
            const el = document.createElement('fp-widget-bar') as HTMLElement & {
                widgets: string[];
            };
            el.widgets = comp.widgets;
            return el;
        }
        case 'promo_image': {
            const el = document.createElement('fp-promo-image');
            el.setAttribute('asset-id', comp.asset_id);
            if (comp.fit) el.setAttribute('fit', comp.fit);
            return el;
        }
    }
}

/* === Layout render + rotation === */

let rotationTimer: number | undefined;

/** promo_image size → panel/şerit oranı yüzdesi. */
function promoSizePct(size?: string): number {
    return size === 'small' ? 25 : size === 'large' ? 50 : 34;
}

/**
 * Dolgu bölümünün dikey alan AĞIRLIĞI (flex-grow).
 * - Sabit boyut (small/medium/large/xlarge) → 1/2/3/4.
 * - auto (varsayılan) → içerik satır sayısı (clamp 1..6) → grid/liste içeriğe göre paylaşır
 *   (eski "her zaman %50" yerine). Böylece az sembollü tablo daha az dikey yer kaplar.
 */
function fillWeight(comp: LayoutComponent): number {
    const FIXED: Record<string, number> = { small: 1, medium: 2, large: 3, xlarge: 4 };
    if (comp.type === 'symbol_grid' || comp.type === 'symbol_list') {
        const area = comp.area ?? 'auto';
        if (area !== 'auto' && FIXED[area] !== undefined) return FIXED[area]!;
        let rows: number;
        if (comp.type === 'symbol_grid') {
            const cols = Math.min(6, Math.max(1, comp.columns ?? 3));
            rows = Math.ceil((comp.symbols?.length ?? 0) / cols);
        } else {
            const groups = (comp.column_symbols ?? []).filter((g) => g.length > 0);
            if (groups.length > 0) rows = Math.max(...groups.map((g) => g.length));
            else {
                const cols = Math.min(3, Math.max(1, comp.columns ?? 1));
                rows = Math.ceil((comp.symbols?.length ?? 0) / cols);
            }
        }
        return Math.max(1, Math.min(6, rows));
    }
    return 1; // akış içi/full promo
}

/** Bir bileşene içerik-akışı (column) flex kuralını uygula. */
function applyFlowFlex(el: HTMLElement, comp: LayoutComponent): void {
    // Sembol bileşenleri + akış içi (placement'sız/full) promo ana alanı doldurur.
    // flex-basis 0 → dolgu bileşenleri alanı flex-grow AĞIRLIĞINA göre böler (auto basis
    // içerik boyutuna göre böler → biri ezilir, hücreleri kırpılır). Ağırlık = fillWeight:
    // grid+liste artık içeriğe/ayara göre paylaşır (hep %50 değil).
    if (comp.type === 'symbol_grid' || comp.type === 'symbol_list') {
        el.style.flex = `${fillWeight(comp)} 1 0`;
        el.style.minHeight = '0';
        el.style.minWidth = '0';
    } else if (comp.type === 'promo_image' && (!comp.placement || comp.placement === 'full')) {
        el.style.flex = '1 1 0';
        el.style.minHeight = '0';
        el.style.minWidth = '0';
    }
}

function renderPage(page: LayoutPage, root: HTMLElement): void {
    root.innerHTML = '';

    // Konumlandırılmış (dock) promo: ilk promo_image, placement left/right/top/bottom.
    // Bu promo akıştan çıkarılır; kalan bileşenler içerik kolonunu oluşturur, promo
    // yan panel (left/right → sayfa yatay bölünür) veya şerit (top/bottom) olarak yerleşir.
    const dockIdx = page.components.findIndex(
        (c) => c.type === 'promo_image' && c.placement !== undefined && c.placement !== 'full',
    );
    const dock = dockIdx >= 0 ? (page.components[dockIdx] as Extract<LayoutComponent, { type: 'promo_image' }>) : null;
    const flowComps = dock ? page.components.filter((_, i) => i !== dockIdx) : page.components;

    // İçerik kolonu (header + fiyatlar + duyuru + akış içi promo)
    const col = document.createElement('div');
    col.className = 'flex flex-col w-full h-full';
    col.style.flex = '1 1 auto';
    col.style.minWidth = '0';
    col.style.minHeight = '0';
    let hasSymbol = false;
    let hasPromo = dock !== null;
    for (const comp of flowComps) {
        const el = renderComponent(comp);
        applyFlowFlex(el, comp);
        if (comp.type === 'symbol_grid' || comp.type === 'symbol_list' || comp.type === 'symbol_ticker') {
            hasSymbol = true;
        }
        if (comp.type === 'promo_image') hasPromo = true;
        col.appendChild(el);
    }

    let outer: HTMLElement;
    if (dock && (dock.placement === 'left' || dock.placement === 'right')) {
        // Yatay bölünme: yan panel + içerik kolonu
        outer = document.createElement('div');
        outer.className = 'flex flex-row w-full h-full';
        const promoEl = renderComponent(dock);
        promoEl.style.flex = `0 0 ${promoSizePct(dock.size)}%`;
        promoEl.style.minWidth = '0';
        if (dock.placement === 'left') {
            outer.appendChild(promoEl);
            outer.appendChild(col);
        } else {
            outer.appendChild(col);
            outer.appendChild(promoEl);
        }
    } else if (dock && (dock.placement === 'top' || dock.placement === 'bottom')) {
        // Dikey şerit: promo sabit yükseklik, içerik kalanı doldurur
        outer = document.createElement('div');
        outer.className = 'flex flex-col w-full h-full';
        const promoEl = renderComponent(dock);
        promoEl.style.flex = `0 0 ${promoSizePct(dock.size)}%`;
        promoEl.style.minHeight = '0';
        if (dock.placement === 'top') {
            outer.appendChild(promoEl);
            outer.appendChild(col);
        } else {
            outer.appendChild(col);
            outer.appendChild(promoEl);
        }
    } else {
        outer = col; // dock yok → sadece içerik kolonu
    }
    root.appendChild(outer);

    // OTA aktivasyon zamanlaması için sayfa türü (#35)
    // Sembol içeren sayfa 'symbol' sayılır (fiyat görünür); aksi 'promo'.
    pageState.set(hasSymbol ? 'symbol' : hasPromo ? 'promo' : 'promo');
}

export function renderLayout(layout: Layout, root: HTMLElement): void {
    if (rotationTimer !== undefined) {
        clearTimeout(rotationTimer);
        rotationTimer = undefined;
    }
    // Yön (orientation) — CSS hook'u (:root[data-orientation] + .fp-portrait)
    const orientation = layout.orientation === 'portrait' ? 'portrait' : 'landscape';
    document.documentElement.setAttribute('data-orientation', orientation);
    document.body.classList.toggle('fp-portrait', orientation === 'portrait');
    document.body.classList.toggle('fp-landscape', orientation === 'landscape');

    // Arka plan + panel saydamlığı — sabit (sayfa rotasyonundan bağımsız) bir kez uygulanır
    applyBackground(layout);

    const firstPage = layout.pages[0];
    if (!firstPage) {
        root.innerHTML = '<div style="padding: 2rem; color: var(--fp-color-fg);">Sayfa tanımlı değil.</div>';
        return;
    }
    renderPage(firstPage, root);

    if (layout.rotation?.enabled && layout.pages.length > 1) {
        startRotation(layout, root);
    }
}

/**
 * Arka plan görseli + kontrast perdesi + panel saydamlık/blur. Sabit `#fp-bg`
 * katmanı body'de tutulur (rotasyondan bağımsız). Layout.surface, tema
 * surface_alpha varsayılanını geçersiz kılar; yoksa CSS değişkeni temizlenir.
 */
function applyBackground(layout: Layout): void {
    const root = document.documentElement.style;
    const bg = layout.background;
    const url = assetUrl(bg?.asset_id ?? null);

    // Sabit arka plan katmanını garanti et (#app'ten ÖNCE → arkada kalır)
    let bgEl = document.getElementById('fp-bg');
    if (!bgEl) {
        bgEl = document.createElement('div');
        bgEl.id = 'fp-bg';
        bgEl.innerHTML = '<div class="fp-bg-img"></div><div class="fp-bg-overlay"></div>';
        document.body.insertBefore(bgEl, document.body.firstChild);
    }

    if (url) {
        root.setProperty('--fp-bg-image', `url("${url}")`);
        root.setProperty('--fp-bg-opacity', String(bg?.opacity ?? 1));
        root.setProperty('--fp-bg-size', bg?.fit === 'fill' ? '100% 100%' : (bg?.fit ?? 'cover'));
        root.setProperty('--fp-bg-blur', `${bg?.blur ?? 0}px`);
        bgEl.style.display = '';
        document.body.classList.add('fp-has-bg');
    } else {
        // Görsel yok → katmanı gizle (perde de görselsiz anlamsız) + blok scrim'i kapat
        root.setProperty('--fp-bg-image', 'none');
        bgEl.style.display = 'none';
        document.body.classList.remove('fp-has-bg');
    }
    // Perde opaklığı (görsel varsa anlamlı)
    root.setProperty('--fp-bg-overlay', String(url ? (bg?.overlay ?? 0) : 0));

    // Panel saydamlığı: layout override varsa uygula, yoksa temaya bırak (CSS var fallback)
    const sOpacity = layout.surface?.opacity;
    if (typeof sOpacity === 'number') root.setProperty('--fp-surface-alpha', String(sOpacity));
    else root.removeProperty('--fp-surface-alpha');

    // Panel buzlu-cam blur'u: yalnız >0 iken backdrop-filter katmanı oluştur (perf)
    const sBlur = layout.surface?.blur ?? 0;
    if (sBlur > 0) {
        root.setProperty('--fp-surface-blur', `${sBlur}px`);
        document.body.classList.add('fp-glass-blur');
    } else {
        root.removeProperty('--fp-surface-blur');
        document.body.classList.remove('fp-glass-blur');
    }
}

export function startRotation(layout: Layout, root: HTMLElement): void {
    if (rotationTimer !== undefined) {
        clearTimeout(rotationTimer);
    }
    let idx = 0;
    const next = (): void => {
        idx = (idx + 1) % layout.pages.length;
        const page = layout.pages[idx]!;
        renderPage(page, root);
        const dur = (page.duration_sec ?? layout.rotation?.interval_sec ?? 30) * 1000;
        rotationTimer = window.setTimeout(next, dur);
    };
    const firstDur = (layout.pages[0]!.duration_sec ?? layout.rotation?.interval_sec ?? 30) * 1000;
    rotationTimer = window.setTimeout(next, firstDur);
}

/* === Tick pipeline === */

export function updateTick(symbol: string, tick: TickLike): void {
    tickStore.update(symbol, tick);
}

export function teardown(): void {
    if (rotationTimer !== undefined) {
        clearTimeout(rotationTimer);
        rotationTimer = undefined;
    }
    // tickStore'u BOŞALTMA: reconnect/hot-reload sonrası initial_state geldiğinde
    // teardown→renderLayout çağrılır; cell'ler connectedCallback'te tickStore.get() ile
    // son değeri okur. Burada clear() yaparsak (özellikle piyasa sakinken, yeni tick
    // gelmezken) cell'ler boş başlar → ekranda "—" → cache'te değer olmasına rağmen
    // fiyat kaybolur. Son değer bellekte kalmalı; monotonic guard (tick-store.ts) eski
    // tick'in yeniyi ezmesini zaten engeller. (decision-pwa-resilience: stale render)
}

export { tickStore };
